<template>
  <div class="container pull-up">
    <div class="filter-panel">
      <div class="filter-header">
        <i class="el-icon-filter filter-icon"></i>
        <span class="filter-title">{{ $t('title.filter') }}</span>
      </div>
      <v-form>
        <v-row justify="center" align="center" class="filter-row">
          <v-col class="mb-0 filter-col">
            <v-select
              v-model="query.store"
              :items="[{ text: $t('user.filter.all_stores'), value: null }, ...departments.map(s => ({ text: s.name, value: s.id }))]"
              :label="$t('user.label.department')"
              outlined dense clearable
              item-text="text" hide-details
              item-value="value"
            />
          </v-col>
          <v-col class="mb-0 filter-col">
            <v-select
              v-model="query.status"
              :items="[
                { text: $t('user.filter.all_statuses'), value: null },
                { text: $t('status.active'), value: true },
                { text: $t('status.inactive'), value: false }
              ]"
              :label="$t('user.label.status')"
              outlined dense clearable
              item-text="text" hide-details
              item-value="value"
            />
          </v-col>
          <v-col class="mb-0 filter-col">
            <v-menu
              ref="dateFromMenu"
              v-model="dateFromMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="query.created_from"
                  :label="$t('user.filter.created_from')"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on" hide-details
                  outlined dense clearable
                />
              </template>
              <v-date-picker
                v-model="query.created_from"
                @input="dateFromMenu = false"
                no-title
                scrollable
              />
            </v-menu>
          </v-col>
          <v-col class="mb-0 filter-col">
            <v-menu
              ref="dateToMenu"
              v-model="dateToMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="query.created_to"
                  :label="$t('user.filter.created_to')"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on" hide-details
                  outlined dense clearable
                />
              </template>
              <v-date-picker
                v-model="query.created_to"
                @input="dateToMenu = false"
                no-title
                scrollable
              />
            </v-menu>
          </v-col>
          <v-col class="filter-actions">
            <v-btn color="primary" @click="handleFilter" :loading="loading" :disabled="loading">
              <v-icon left>mdi-filter</v-icon>
              {{ $t('button.search') }}
            </v-btn>
            <v-btn v-if="hasActiveFilters" @click="clearFilters" :loading="loading" :disabled="loading" style="margin-left: 8px;">
              <v-icon left>mdi-filter-remove</v-icon>
              {{ $t('button.reset') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </div>
    <div class="row">
      <div class="col-md-12">
        <div class="card m-b-30">
          <div class="card-header">
            <el-row>
              <el-col :span="12">
                <h5 class="m-b-0">
                  <i class="mdi mdi-account-group-outline"></i> {{ $t('user.button.user') }}
                </h5>
              </el-col>
            </el-row>
          </div>
          <div class="card-body">
            <user-assign-role-modal :user="assign" v-if="assign.id"
                                    @close="assign = {}"></user-assign-role-modal>
            <div class="table-responsive">
              <data-tables
                  :data="users"
                  :pagination-props="{hideOnSinglePage: true}"
                  center-align>
                <el-table-column :label="$t('user.label.photo')" align="center" min-width="50" fixed>
                  <template slot-scope="scope">
                    <el-avatar size="medium" :src="scope.row.photo"></el-avatar>
                  </template>
                </el-table-column>
                <el-table-column show-overflow-tooltip prop="name" :label="$t('user.label.name')" fixed>
                  <template slot-scope="scope">{{ scope.row.name }}
                    <el-tag size="mini" v-if="scope.row.is_admin" type="primary">SU</el-tag>
                  </template>
                </el-table-column>
                <el-table-column prop="username" :label="$t('user.label.username')" fixed></el-table-column>
                <el-table-column prop="department" :label="$t('user.label.department')">
                  <template slot-scope="scope">
                    {{ scope.row.department ? scope.row.department.name : 'None' }}
                  </template>
                </el-table-column>
                <el-table-column :label="$t('user.label.status')">
                  <template slot-scope="scope">
                    <el-checkbox v-can.disable="'user.archive'" style="padding-bottom: 25px!important;" :disabled="scope.row.is_admin"
                                 :label="$t(`status.${scope.row.active? 'active':'inactive'}`)"
                                 border @change="updateStatus($event, scope.row.id)"
                                 size="mini" :value="!!scope.row.active"></el-checkbox>
                  </template>
                </el-table-column>
                <el-table-column prop="created_at" :formatter="$shortDate" :label="$t('user.label.created_at')"
                                 sortable></el-table-column>
                <el-table-column :label="$t('user.label.actions')" min-width="120px">
                  <template slot="header" slot-scope="scope">
                    <el-input v-model="query.search" size="mini" autofocus
                              placeholder="Type to search" @input="handleSearch"/>
                  </template>
                  <template slot-scope="scope">
                    <el-button-group>
                      <el-tooltip :content="$t('button.edit')" placement="top">
                        <el-button v-can.disable="'user.update'" size="mini" type="success" icon="el-icon-edit"
                                   @click="editUserDialog(scope.row.id)">
                        </el-button>
                      </el-tooltip>
                      <el-tooltip :content="$t('user.button.reset')" placement="top">
                        <el-button v-can.disable="'user.reset-password'" type="warning" size="mini" :disabled="scope.row.is_admin" icon="el-icon-key"
                                   @click="resetPassword(scope.row.id)">
                        </el-button>
                      </el-tooltip>
                      <el-tooltip :content="$t('user.button.assign')" placement="top">
                        <el-button v-can.disable="'role.assign'" type="primary" size="mini" icon="el-icon-lock"
                                   @click="assignRole(scope.row.id, scope.$index)">
                        </el-button>
                      </el-tooltip>
                    </el-button-group>
                  </template>
                </el-table-column>
              </data-tables>
              <div class="pagination-center">
                <pagination
                  :total="pagination.total"
                  :page-size="+pagination.per_page"
                  :page-sizes="[10, 20, 30, 40, 100]"
                  :current-page="+pagination.current_page"
                  @current-change="handlePageChange"
                  @size-change="handlePageSizeChange"
                  :loading="loading"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <create-edit-user :user="user" :visible="displayDialog" @created="userCreated" @updated="userUpdated"
                      @close="closeUserDialog"></create-edit-user>
  </div>
</template>

<script>
import CreateEditUser from './Modals/CreateEditUser'
import UserAssignRoleModal from './Modals/AssignRole'
import Pagination from '@/components/_Partials/Pagination.vue'

export default {
  name: 'Index',
  components: {
    Pagination,
    CreateEditUser,
    UserAssignRoleModal
  },
  data () {
    return {
      users: [],
      user: {},
      displayDialog: false,
      assign: {},
      departments: [],
      loading: false,
      dateFromMenu: false,
      dateToMenu: false,
      query: {
        store: null,
        status: null,
        created_from: null,
        created_to: null,
        search: ''
      },
      pagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1
      }
    }
  },
  methods: {
    handleFilter () {
      this.pagination.current_page = 1
      this.getUsers()
    },
    handleSearch (value) {
      this.pagination.current_page = 1
      this.getUsers()
    },
    clearFilters () {
      this.query = {
        store: null,
        status: null,
        created_from: null,
        created_to: null,
        search: ''
      }
      this.pagination.current_page = 1
      this.getUsers()
    },
    handlePageChange (page) {
      this.pagination.current_page = page
      this.getUsers()
    },
    handlePageSizeChange (size) {
      this.pagination.per_page = size
      this.pagination.current_page = 1
      this.getUsers()
    },
    assignRole (id, index) {
      this.assign = this.users[index]
    },
    resetPassword (id) {
      this.$prompt(this.$t('user.confirm.reset'), this.$t('user.title.reset', { name: this.users.find(u => u.id === id).username }), {
        confirmButtonText: this.$t('button.proceed'),
        cancelButtonText: this.$t('button.cancel'),
        inputPattern: /[\w]{6,}/,
        inputErrorMessage: this.$t('user.validation.password')
      }).then(({ value }) => {
        this.$userService.resetPassword(id, { password: value })
          .then(data => {
            this.displayMessage(this.$t('user.message.reset'))
          })
          .catch(error => {
            this.displayMessage(error.response && error.response.data ? error.response.data.message : error.message,
              () => {
              }, 'error')
          })
      })
    },
    updateStatus (value, id) {
      this.askQuestion(this.$t('user.confirm.status'), () => {
        this.$userService.changeStatus(id)
          .then(data => {
            const index = this.users.indexOf(this.users.find(u => u.id === id))
            this.users[index].active = data.active
          })
          .catch(error => {
            this.displayMessage(error.response && error.response.data ? error.response.data.message : error.message,
              () => {
              }, 'error')
          })
      })
    },
    editUserDialog (id) {
      this.user = this.users.find(e => e.id === id)
      this.displayDialog = true
    },
    closeUserDialog () {
      this.user = {}
      this.displayDialog = false
    },
    userCreated (data) {
      this.users.splice(0, 0, data)
    },
    userUpdated (data) {
      const index = this.users.indexOf(this.users.find(u => u.id === data.id))
      this.users.splice(index, 1, data)
    },
    async getUsers (params = {}) {
      try {
        this.loading = true
        const mergedParams = {
          ...this.query,
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
          ...params
        }
        const data = await this.$userService.getUsersPaginated(mergedParams)
        this.users = data.data
        this.pagination.current_page = data.meta.current_page
        this.pagination.per_page = data.meta.per_page
        this.pagination.total = data.meta.total
        this.pagination.last_page = data.meta.last_page
      } catch (error) {
        // Error is already handled by the global interceptor
        console.log('Component caught error in getUsers:', error)
      } finally {
        this.loading = false
      }
    }
  },
  computed: {
    hasActiveFilters () {
      return (
        this.query.store !== null ||
        this.query.status !== null ||
        this.query.created_from !== null ||
        this.query.created_to !== null ||
        this.query.search !== ''
      )
    }
  },
  async mounted () {
    this.$emit('parent-title', this.$t('user.title.user'))

    try {
      this.loading = true
      // Fetch departments for the filter dropdown
      this.departments = await this.$departmentService.getDepartments()
      await this.getUsers()
    } catch (error) {
      console.log('Error in mounted:', error)
    } finally {
      this.loading = false
    }
  },
  created () {
    this.$parent.$on('user-created', this.userCreated)
  }
}
</script>

<style scoped>
.pagination-center {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}
.filter-panel {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  padding: 24px 24px 12px 24px;
  margin-bottom: 24px;
  border: 1px solid #f0f0f0;
}
.filter-header {
  display: flex;
  align-items: center;
  margin-bottom: 18px;
}
.filter-icon {
  font-size: 22px;
  color: #409EFF;
  margin-right: 8px;
}
.filter-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}
.filter-row {
  margin-bottom: 0;
}
.filter-col {
  min-width: 180px;
  max-width: 260px;
}
.filter-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
